import React, {useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {CreditCard, Loader, Printer, RefreshCw, Ban, CheckCircle, Plus} from "lucide-react";
import toast from "react-hot-toast";
import usePermissions from "../../hooks/usePermissions";
import secureStorage from "../../utils/secureStorage";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import Button from "../Common/Button";
import LoyaltyBarcode, {printLoyaltyCard} from "./LoyaltyBarcode";
import {
    CARD_STATUS_LABELS,
    apiErrorMessage,
    createLoyaltyCard,
    fetchClientLoyaltyCards,
    fetchLoyaltyProgram,
    replaceLoyaltyCard,
    setLoyaltyCardBlocked,
} from "./loyaltyApi";

const STATUS_CLASSES = {
    active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    blocked: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    replaced: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

// Loyalty card section of the client edit form (backoffice): current card +
// barcode, issue / replace / block / print, and the card history.
const ClientLoyaltyCard = ({clientId, clientName}) => {
    const queryClient = useQueryClient();
    const {canRead, canUpdate} = usePermissions();
    const canSee = canRead("loyalty");
    const canManage = canUpdate("loyalty");
    const [confirm, setConfirm] = useState(null); // 'replace' | 'block'

    const {data, isLoading, isError} = useQuery({
        queryKey: ["loyalty-client-cards", clientId],
        queryFn: () => fetchClientLoyaltyCards(clientId),
        enabled: !!clientId && canSee,
    });
    const {data: program} = useQuery({
        queryKey: ["loyalty-program"],
        queryFn: fetchLoyaltyProgram,
        enabled: canSee,
    });

    const refresh = () => {
        queryClient.invalidateQueries({queryKey: ["loyalty-client-cards", clientId]});
        queryClient.invalidateQueries({queryKey: ["loyalty-cards"]});
    };
    const onError = (fallback) => (error) => toast.error(apiErrorMessage(error, fallback));

    const createMutation = useMutation({
        mutationFn: () => createLoyaltyCard(clientId),
        onSuccess: () => {
            toast.success("Carte de fidélité créée");
            refresh();
        },
        onError: onError("Impossible de créer la carte"),
    });

    const replaceMutation = useMutation({
        mutationFn: (cardId) => replaceLoyaltyCard(cardId),
        onSuccess: () => {
            toast.success("Nouvelle carte émise, l'ancienne n'est plus valable");
            refresh();
        },
        onError: onError("Impossible de remplacer la carte"),
    });

    const blockMutation = useMutation({
        mutationFn: ({cardId, blocked}) => setLoyaltyCardBlocked(cardId, blocked),
        onSuccess: (_, {blocked}) => {
            toast.success(blocked ? "Carte bloquée" : "Carte réactivée");
            refresh();
        },
        onError: onError("Impossible de modifier la carte"),
    });

    const cards = data?.cards || [];
    const activeCard = data?.active_card || null;
    // A blocked card is still the client's current card (can be reactivated).
    const currentCard = activeCard || cards.find((c) => c.status === "blocked") || null;
    const pastCards = cards.filter((c) => c.id !== currentCard?.id);
    const busy = createMutation.isPending || replaceMutation.isPending || blockMutation.isPending;

    // Hidden while the shop has no loyalty program, unless the client
    // already holds cards (e.g. the program was switched off afterwards).
    if (!canSee || !program || (program.mode === "off" && cards.length === 0)) {
        return null;
    }

    const handlePrint = async () => {
        const account = await secureStorage.getItem("selectedAccount");
        const ok = printLoyaltyCard({
            code: currentCard.code,
            clientName,
            shopName: account?.name || "",
        });
        if (!ok) toast.error("Autorisez les fenêtres pop-up pour imprimer la carte");
    };

    return (
        // Flat section with a top rule, like the field groups of the client form
        <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-primary"/>
                <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">Carte de fidélité</h2>
            </div>

            {isLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Loader className="w-4 h-4 animate-spin text-primary"/> Chargement…
                </div>
            )}

            {isError && (
                <p className="text-sm text-red-500">Impossible de charger la carte de fidélité.</p>
            )}

            {!isLoading && !isError && !currentCard && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Ce client n'a pas encore de carte.</p>
                    {canManage && (
                        <Button variant="tinted" size="sm" icon={Plus} loading={createMutation.isPending}
                                disabled={busy} onClick={() => createMutation.mutate()}>
                            Créer la carte
                        </Button>
                    )}
                </div>
            )}

            {currentCard && (
                <div className="flex flex-col md:flex-row gap-4 md:items-center">
                    <div className="bg-white rounded-lg border border-gray-300 dark:border-gray-600 p-2 self-start shadow-sm">
                        <LoyaltyBarcode code={currentCard.code} height={50}/>
                    </div>
                    <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-lg font-semibold text-gray-900 dark:text-gray-100">{currentCard.code}</span>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${STATUS_CLASSES[currentCard.status]}`}>
                                {CARD_STATUS_LABELS[currentCard.status]}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="tinted" size="sm" icon={Printer} onClick={handlePrint}>Imprimer</Button>
                            {canManage && (
                                <>
                                    <Button variant="secondary" size="sm" icon={RefreshCw} disabled={busy}
                                            onClick={() => setConfirm("replace")}>
                                        Remplacer (carte perdue)
                                    </Button>
                                    {currentCard.status === "active" ? (
                                        <Button variant="danger" size="sm" icon={Ban} disabled={busy}
                                                onClick={() => setConfirm("block")}>
                                            Bloquer
                                        </Button>
                                    ) : (
                                        <Button variant="tinted" size="sm" icon={CheckCircle} disabled={busy}
                                                onClick={() => blockMutation.mutate({cardId: currentCard.id, blocked: false})}>
                                            Réactiver
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {pastCards.length > 0 && (
                <div className="mt-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-2">Anciennes cartes</p>
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
                        {pastCards.map((card) => (
                            <li key={card.id} className="flex items-center justify-between py-2 text-sm">
                                <span className="font-mono text-gray-600 dark:text-gray-300">{card.code}</span>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${STATUS_CLASSES[card.status]}`}>
                                    {CARD_STATUS_LABELS[card.status]}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {confirm === "replace" && (
                <ConfirmationDialog
                    title="Remplacer la carte ?"
                    message="Une nouvelle carte avec un nouveau code sera créée. L'ancienne carte ne sera plus acceptée en caisse. Les points du client sont conservés."
                    confirmLabel="Remplacer"
                    cancelLabel="Annuler"
                    onCancel={() => setConfirm(null)}
                    onConfirm={() => {
                        replaceMutation.mutate(currentCard.id);
                        setConfirm(null);
                    }}
                />
            )}
            {confirm === "block" && (
                <ConfirmationDialog
                    title="Bloquer la carte ?"
                    message="La carte ne sera plus acceptée en caisse jusqu'à sa réactivation."
                    confirmLabel="Bloquer"
                    cancelLabel="Annuler"
                    danger
                    onCancel={() => setConfirm(null)}
                    onConfirm={() => {
                        blockMutation.mutate({cardId: currentCard.id, blocked: true});
                        setConfirm(null);
                    }}
                />
            )}
        </div>
    );
};

export default ClientLoyaltyCard;
